import { useState } from "react";
import { useNavigate } from "react-router";
import {
  Button,
  DataButton,
  InputContainer,
  InputOption,
  InputSelect,
  SingleFieldDialog,
  SingleFieldDialogCTA,
  SingleFieldDialogHeader,
  SingleFieldDialogImage,
} from "@sellar-io/components";
import { Alert } from "../../../components/alert/Alert";
import { Icon } from "../../../components";
import { useDeliveryZoneModeAssignments } from "../../../hooks/api/v2/deliveryZones/useDeliveryZoneModeAssignments";
import { useDeliveryZoneModeUpdate } from "../../../hooks/api/v2/deliveryZones/useDeliveryZoneModeUpdate";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import * as ROUTES from "../../../constants/routes";

export const DeliveryZonesPrompt = ({ onClick }: { onClick: () => void }) => (
  <Alert
    type="sellar"
    title="Need different default delivery methods for additional geographical areas?"
    message="Enable Delivery Zones to create custom postcode areas in addition to your Local and Nationwide zones."
    className="w-full border-zinc-300 bg-zinc-50"
  >
    <DataButton
      border
      className="flex w-fit items-center gap-1"
      onClick={onClick}
    >
      Set up Delivery Zones
      <Icon icon="ARROW_TOP_RIGHT" size={0.7} />
    </DataButton>
  </Alert>
);

export const DeliveryZoneAdvancedSetupDialog = ({
  active,
  onDismiss,
}: {
  active: boolean;
  onDismiss: () => void;
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState<"introduction" | "confirmation">(
    "introduction",
  );
  const mutation = useDeliveryZoneModeUpdate();
  const dismiss = () => {
    setPage("introduction");
    onDismiss();
  };
  return (
    <SingleFieldDialog active={active} onDismiss={dismiss} size="large">
      <SingleFieldDialogImage
        src={
          page === "introduction"
            ? "https://sellar.imgix.net/sellar-image-assets/terms-basic-advanced/basic-dz-1.png"
            : "https://sellar.imgix.net/sellar-image-assets/terms-basic-advanced/basic-dz-2.png"
        }
      />
      <SingleFieldDialogHeader
        title={
          page === "introduction"
            ? "Create additional delivery zones"
            : "Manage postcodes in both delivery schedules and delivery zones"
        }
        description={
          page === "introduction"
            ? "Delivery zones give you more control over what delivery information your customers see based on their postcode"
            : "Postcodes in your Local delivery zone will stop automatically updating from your delivery schedules. We’ll help you manage your postcodes in both zones and schedules."
        }
        icon="INFORMATION_OUTLINE"
        iconClassName="mb-auto bg-blue-50 text-blue-600"
      />
      <SingleFieldDialogCTA>
        <Button
          color="transparent"
          onClick={
            page === "introduction" ? dismiss : () => setPage("introduction")
          }
        >
          {page === "introduction" ? "Dismiss" : "Back"}
        </Button>
        {page === "introduction" ? (
          <Button onClick={() => setPage("confirmation")}>Next</Button>
        ) : (
          <Button
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate(
                { mode: "advanced" },
                {
                  onSuccess: () => {
                    dismiss();
                    navigate(ROUTES.DELIVERY_ZONES);
                  },
                },
              )
            }
          >
            Turn on <Icon icon="CHECK_CIRCLE" />
          </Button>
        )}
      </SingleFieldDialogCTA>
    </SingleFieldDialog>
  );
};

type ReconciliationAction = "mergeIntoLocal" | "delete";

export const RevertToBasicDeliveryZoneDialog = ({
  active,
  onDismiss,
}: {
  active: boolean;
  onDismiss: () => void;
}) => {
  const assignments = useDeliveryZoneModeAssignments({ enabled: active });
  const mutation = useDeliveryZoneModeUpdate();
  const [page, setPage] = useState<"introduction" | "reconcile">(
    "introduction",
  );
  const [selections, setSelections] = useState<
    Record<number, ReconciliationAction>
  >({});
  const [error, setError] = useState("");
  const zones = assignments.data ?? [];
  const dismiss = () => {
    setPage("introduction");
    setSelections({});
    setError("");
    onDismiss();
  };
  const complete = zones.every((zone) => selections[zone.deliveryZoneId]);
  const revert = () => {
    setError("");
    mutation.mutate(
      {
        mode: "basic",
        selections: zones.map((zone) => ({
          deliveryZoneId: zone.deliveryZoneId,
          action: selections[zone.deliveryZoneId]!,
        })),
      },
      {
        onSuccess: dismiss,
        onError: (error) => {
          const apiError = apiV2AssertError(error);
          setError(
            apiError?.response?.data.message ??
              "Could not revert. Please reload and try again.",
          );
          assignments.refetch();
        },
      },
    );
  };

  if (page === "introduction") {
    return (
      <SingleFieldDialog active={active} onDismiss={dismiss} size="large">
        <SingleFieldDialogImage src="https://sellar.imgix.net/sellar-image-assets/terms-basic-advanced/basic-dz-3.png" />
        <SingleFieldDialogHeader
          title="You can revert back to a simpler Delivery Zone set up"
          description="By choosing this you will no longer be able to create custom delivery zones defined by postcode. Your Local delivery zone will go back to being defined by the postcodes entered in your direct delivery schedules."
          icon="INFORMATION_OUTLINE"
          iconClassName="mb-auto bg-blue-50 text-blue-600"
        />
        {(assignments.isError || error) && (
          <Alert
            type="error"
            className="mx-4 w-auto"
            message={
              error ||
              "Could not load your custom delivery zones. Please try again."
            }
          />
        )}
        <SingleFieldDialogCTA>
          <Button color="transparent" onClick={dismiss}>
            Dismiss
          </Button>
          <Button
            className="flex items-center gap-1"
            disabled={assignments.isFetching || assignments.isError}
            loading={mutation.isPending}
            onClick={() => (zones.length ? setPage("reconcile") : revert())}
          >
            {zones.length ? "Next" : "Confirm"}
            <Icon
              icon={zones.length ? "CHEVRON_RIGHT" : "CHECK_CIRCLE"}
              size={0.8}
            />
          </Button>
        </SingleFieldDialogCTA>
      </SingleFieldDialog>
    );
  }

  return (
    <SingleFieldDialog active={active} onDismiss={dismiss} size="large">
      <SingleFieldDialogHeader
        title="You have custom delivery zones set up"
        description="Decide what to do with your existing custom delivery zones before turning off this functionality."
      />
      <div className="flex max-h-[55vh] flex-col gap-5 overflow-y-auto px-4">
        {zones.map((zone) => (
          <div key={zone.deliveryZoneId} className="flex flex-col gap-2">
            <strong>{zone.deliveryZoneName}</strong>
            <span className="rounded bg-zinc-100 px-3 py-2">
              <span className="font-semibold">Postcodes →</span>{" "}
              {zone.postcodes.join(", ") || "None"}
            </span>
            <InputContainer>
              <InputSelect
                name={`zone-${zone.deliveryZoneId}`}
                value={selections[zone.deliveryZoneId] ?? ""}
                onChange={(event) =>
                  setSelections((current) => ({
                    ...current,
                    [zone.deliveryZoneId]: event.target
                      .value as ReconciliationAction,
                  }))
                }
              >
                <InputOption value="" disabled>
                  Choose an action
                </InputOption>
                <InputOption
                  value="mergeIntoLocal"
                  disabled={!zone.allPostcodesCoveredBySchedules}
                >
                  Merge zone into Local
                </InputOption>
                <InputOption value="delete">Delete zone</InputOption>
              </InputSelect>
            </InputContainer>
            {!zone.allPostcodesCoveredBySchedules && (
              <Alert
                type="warning"
                className="w-full p-2 [&_span]:font-semibold"
                message="Check that these postcodes are listed in a direct delivery schedule"
              />
            )}
            {selections[zone.deliveryZoneId] === "delete" && (
              <Alert
                type="information"
                className="w-full p-2 [&_span]:font-semibold"
                message="Customers in the postcodes listed will fall back to default Nationwide delivery"
              />
            )}
          </div>
        ))}
        {(assignments.isError || error) && (
          <Alert
            type="error"
            className="w-full"
            message={
              error ||
              "Could not load your custom delivery zones. Please try again."
            }
          />
        )}
      </div>
      <SingleFieldDialogCTA>
        <Button color="transparent" onClick={dismiss}>
          Dismiss
        </Button>
        <Button
          disabled={assignments.isFetching || assignments.isError || !complete}
          loading={mutation.isPending}
          onClick={revert}
        >
          Confirm
        </Button>
      </SingleFieldDialogCTA>
    </SingleFieldDialog>
  );
};
